/**
 * Instructor Communities pilot events fired from the **public** instructor
 * profile (`/{lang}/user/<url-title>`).
 *
 * Schemas live in `udemy/schema-store` (`events/v2/`), registered on DEV via
 * PR #1837 and pending Event Review Council review. Two of the five events in
 * that set are produced here; the other three belong to the community landing
 * page, the course landing page, and `website-django`.
 *
 * 🔴 The event names and `uiRegion` values below are the *only* link between
 * this producer and the registered schema. A name that does not match the AVDL
 * record name, or a `uiRegion` outside the schema's `@options` set, drops the
 * event silently — there is no client-side error. They are centralised here (and
 * never inlined at call sites) so an ERC-requested rename is a one-file change.
 *
 * @see https://github.com/udemy/schema-store/pull/1837
 */
import {TrackingEvent} from '@udemy-v2/event-tracking';

/**
 * AVDL record names, verbatim. `TrackingEvent`'s `type` must equal these.
 */
const EVENT_NAME = {
    presented: 'InstructorCommunityPresented',
    joinSelected: 'InstructorCommunityJoinSelected',
} as const;

/**
 * `uiRegion` values for the placements this repo owns. Must stay a subset of
 * each event's schema `@options` set.
 *
 * `clp.banner` and `cte.banner` are also valid on `InstructorCommunityJoinSelected`
 * but are fired by other repos — deliberately absent here.
 */
export const COMMUNITY_UI_REGION = {
    /** The community section itself, on the public profile. Impression only. */
    section: 'instructor_profile.community_section',
    /** Join CTA in the profile header, above the community section. */
    profileHeader: 'instructor_profile.header',
    /** Join CTA in the community section's own header. */
    sectionHeader: 'instructor_profile.community_section.header',
    /** Join CTA in the community benefits block. */
    sectionBenefits: 'instructor_profile.community_section.benefits',
} as const;

/** A `uiRegion` value valid on `InstructorCommunityJoinSelected` from this page. */
export type CommunityJoinUiRegion =
    | typeof COMMUNITY_UI_REGION.profileHeader
    | typeof COMMUNITY_UI_REGION.sectionHeader
    | typeof COMMUNITY_UI_REGION.sectionBenefits;

/** Avro `long id` entity references. */
interface EntityRef {
    id: number;
}

export interface InstructorCommunityPresentedData {
    /** The profile owner. */
    instructor: EntityRef;
    instructorCommunity: EntityRef;
    uiRegion: typeof COMMUNITY_UI_REGION.section;
}

export interface InstructorCommunityJoinSelectedData {
    /** The profile owner, i.e. the community's owner. */
    instructor: EntityRef;
    instructorCommunity: EntityRef;
    /**
     * Always `null` on the profile: no single course is in context there. The
     * CLP/CTE banners (other repos) are the ones that set it.
     */
    course: null;
    uiRegion: CommunityJoinUiRegion;
}

/**
 * Fired when the community section becomes visible to a learner on the public
 * profile — the destination surface, not an advert for it. Fire on viewport
 * visibility via `TrackImpression`, never on mount.
 */
export class InstructorCommunityPresented extends TrackingEvent<InstructorCommunityPresentedData> {
    constructor(data: InstructorCommunityPresentedData) {
        super(EVENT_NAME.presented, data);
    }
}

/**
 * Fired when a learner selects a "Join my community" CTA. The placement is
 * carried in `uiRegion` — see {@link COMMUNITY_UI_REGION}. There is deliberately
 * no matching impression event for the CTAs themselves, so join rate is
 * measurable at the section level only.
 */
export class InstructorCommunityJoinSelected extends TrackingEvent<InstructorCommunityJoinSelectedData> {
    constructor(data: InstructorCommunityJoinSelectedData) {
        super(EVENT_NAME.joinSelected, data);
    }
}

/**
 * Coerce an id that reaches the UI as a string (GraphQL `ID`) or number into the
 * Avro `long` the schemas expect. Returns `null` for anything that is not a
 * finite integer, so call sites can skip firing rather than publish an event
 * with a `NaN` id (which the collector rejects).
 */
export function toEventId(value: string | number | null | undefined): number | null {
    if (value == null || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * Build a {@link InstructorCommunityJoinSelected} for a placement, or `null`
 * when either id is missing/unparseable — which is the case on the instructor
 * DRAFT preview path, where no events should fire at all.
 */
export function buildJoinSelectedEvent({
    instructorId,
    communityId,
    uiRegion,
}: {
    instructorId: string | number | null | undefined;
    communityId: string | number | null | undefined;
    uiRegion: CommunityJoinUiRegion;
}): InstructorCommunityJoinSelected | null {
    const instructor = toEventId(instructorId);
    const instructorCommunity = toEventId(communityId);

    if (instructor == null || instructorCommunity == null) {
        return null;
    }

    return new InstructorCommunityJoinSelected({
        instructor: {id: instructor},
        instructorCommunity: {id: instructorCommunity},
        course: null,
        uiRegion,
    });
}

/**
 * Build a {@link InstructorCommunityPresented}, or `null` when either id is
 * missing/unparseable (see {@link buildJoinSelectedEvent}).
 */
export function buildPresentedEvent({
    instructorId,
    communityId,
}: {
    instructorId: string | number | null | undefined;
    communityId: string | number | null | undefined;
}): InstructorCommunityPresented | null {
    const instructor = toEventId(instructorId);
    const instructorCommunity = toEventId(communityId);

    if (instructor == null || instructorCommunity == null) {
        return null;
    }

    return new InstructorCommunityPresented({
        instructor: {id: instructor},
        instructorCommunity: {id: instructorCommunity},
        uiRegion: COMMUNITY_UI_REGION.section,
    });
}
